import { useEffect, useId, type ReactNode } from "react";
import {
  ALPHA_PATH,
  ALPHA_SPLIT_X,
  ALPHA_TRANSFORM,
  ALPHA_VIEW_BOX,
} from "../lib/alphaGlyph";
import type { Mode } from "../lib/rounds";
import { isDialogOpen } from "../state/dialogStore";
import { useSettingsStore } from "../state/settingsStore";
import { Container } from "./Container";
import { Kbd } from "./Kbd";

/**
 * The one header, rendered once at the app root above every mode's view
 * (GRA-94): the `candle α` wordmark on the left, the Predict / Trade switcher
 * on the right, both aligned to the shared layout Container (GRA-91). The
 * highlighted segment says which mode you are in, so no mode label is needed
 * in the stats rows below.
 */

/**
 * The α mark on its own, sized to the surrounding text's x-height.
 *
 * The outline lives in `lib/alphaGlyph` (the favicon draws the same one), so
 * the header, the static pages, the Result card and the Open Graph image all
 * paint one shape. Kept as a path rather than a text `α` so it stays identical
 * in every font, and split at the vertical center into the candle-up /
 * candle-down theme tokens, the same pair every other direction in the UI is
 * drawn in.
 */
export function AlphaMark({ className = "" }: { className?: string }) {
  // Ids are document-scoped, so scope the two clip paths to this instance.
  const uid = useId();
  const leftId = `${uid}-left`;
  const rightId = `${uid}-right`;

  return (
    <svg
      viewBox={`0 0 ${ALPHA_VIEW_BOX} ${ALPHA_VIEW_BOX}`}
      role="img"
      aria-label="alpha"
      className={className}
    >
      <defs>
        <clipPath id={leftId}>
          <rect x="0" y="0" width={ALPHA_SPLIT_X} height={ALPHA_VIEW_BOX} />
        </clipPath>
        <clipPath id={rightId}>
          <rect
            x={ALPHA_SPLIT_X}
            y="0"
            width={ALPHA_VIEW_BOX - ALPHA_SPLIT_X}
            height={ALPHA_VIEW_BOX}
          />
        </clipPath>
      </defs>
      {[
        { clip: leftId, fill: "fill-up" },
        { clip: rightId, fill: "fill-down" },
      ].map(({ clip, fill }) => (
        <g key={clip} clipPath={`url(#${clip})`}>
          <g transform={ALPHA_TRANSFORM}>
            <path d={ALPHA_PATH} className={fill} />
          </g>
        </g>
      ))}
    </svg>
  );
}

/**
 * Lowercase `candle` in the UI font followed by the α. Static, never a link.
 *
 * Rendered as the page's `h1` in the header (GRA-148): the app had no heading
 * outside the dialogs, so a screen reader's heading list was empty and there
 * was nothing to jump to. The wordmark is the page's name, so it carries the
 * level rather than a second, invisible title being invented for it. `as`
 * exists so the mark can still be printed as plain text anywhere a second h1
 * would be wrong.
 */
export function Wordmark({ as: Tag = "span" }: { as?: "span" | "h1" }) {
  return (
    <Tag className="m-0 flex items-center gap-[0.2em] font-mono text-base font-normal lowercase tracking-tight text-fg">
      candle
      <AlphaMark className="ml-px h-[1.05em] w-[1.05em] shrink-0" />
    </Tag>
  );
}

const MODES: { value: Mode; label: string }[] = [
  { value: "predict", label: "Predict" },
  { value: "trade", label: "Trade" },
  // Last: the two free-play modes are the everyday ones, and the challenge is
  // the thing you come back for once a day (GRA-139). "Challenge", not
  // "Daily", because the settings strip already has a Daily *timeframe* and
  // two controls reading "Daily" would be one ambiguous label too many.
  { value: "challenge", label: "Challenge" },
];

/**
 * The mode control: one segmented pill, not three separate chips (GRA-155
 * fix-up: three bordered buttons with their own gaps read as three unrelated
 * controls, and at the header's narrower widths the stats group they shared a
 * track with had nowhere to shrink into). One shared border draws the pill's
 * outline, an inner divider marks each seam, and only the first/last segment
 * corners round off. The active segment wears the same pair as the app's
 * primary action (accent text, accent outline, no fill); the outline is an
 * inset ring so it sits inside the pill's shared border rather than doubling
 * it. Still plain
 * buttons under one `role="group"`, the same aria and keyboard behaviour as
 * before, just restyled.
 */
export function ModeSwitcher() {
  const mode = useSettingsStore((s) => s.mode);
  const setMode = useSettingsStore((s) => s.setMode);

  // `[` and `]` walk the switcher, in the order the segments are drawn, and
  // wrap at both ends. The brackets sit either side of the pill, so the keys
  // read as the pill's own left/right rather than as two more letter hotkeys
  // competing with the ones the settings strip already claims. They go
  // through the same `setMode` the segments' onClick calls, so whatever a
  // mid-round switch does on click it does here too.
  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      if (isDialogOpen()) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key !== "[" && event.key !== "]") return;
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      event.preventDefault();
      const at = MODES.findIndex((m) => m.value === useSettingsStore.getState().mode);
      const step = event.key === "]" ? 1 : -1;
      const next = (at + step + MODES.length) % MODES.length;
      setMode(MODES[next].value);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setMode]);

  return (
    <div className="flex items-center text-muted">
      <span aria-hidden="true">
        <Kbd className="mr-1.5">[</Kbd>
      </span>
      <div
        role="group"
        aria-label="mode"
        className="flex overflow-hidden rounded border border-muted/30 font-mono text-xs"
      >
        {MODES.map(({ value, label }, index) => (
          <button
            key={value}
            type="button"
            aria-pressed={value === mode}
            onClick={() => setMode(value)}
            className={`flex min-h-7 items-center px-2 py-0 transition-colors sm:min-h-6 sm:px-3 sm:py-1 ${
              index > 0 ? "border-l border-muted/30" : ""
            } ${
              value === mode
                ? "text-accent ring-1 ring-inset ring-accent"
                : "text-muted hover:text-fg"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <span aria-hidden="true">
        <Kbd>]</Kbd>
      </span>
    </div>
  );
}

/**
 * The row a mode's stats are laid out in: the spacing and muted type every
 * stats group in the header shares. Exported so StatusBar and TradeStats carry
 * their own layout instead of depending on AppHeader's slot styling them.
 */
export function HeaderStats({ children }: { children: ReactNode }) {
  return (
    // Below `sm` the stats sit on the header's second line, aligned left with
    // the wordmark above them rather than hugging the right edge with a ragged
    // left margin. From `sm` they take the grid's right-hand track.
    //
    // `w-full` (not the old `sm:w-auto sm:flex-none`, GRA-155 fix-up): this is
    // a flex container with a single child (the flex row below) that sits
    // inside the grid's own right-hand track, itself already `min-w-0`. A
    // shrink-to-fit flex child sizes to its *unwrapped* content first and only
    // wraps once it's already been squeezed, but nothing here was squeezing
    // it, so at the header's narrower widths it kept its unwrapped width and
    // painted straight over the switcher's column instead of wrapping inside
    // its own. Filling the track's width up front is what gives `flex-wrap`
    // something to wrap against.
    <div className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-muted sm:justify-end sm:gap-x-4 sm:text-sm">
      {children}
    </div>
  );
}

/**
 * One bar for the whole app, in three slots: the wordmark left, the Predict /
 * Trade switcher centered in the bar, and the active mode's stats (`children` —
 * Predict's StatusBar or Trade's TradeStats) right. A three-column grid from
 * `sm` up, with the middle column sized to its content and equal 1fr columns
 * either side, so the switcher stays centered on the bar however wide the stats
 * grow. Both side tracks are `min-w-0` so a long stats group (Trade's, once an
 * account has trades) wraps inside its own column rather than forcing the grid
 * wider than the container. Below `sm` it falls back to a wrapping flex row.
 *
 * Below `sm` that wrapping row used to cost three lines and 147px of chrome
 * before a single candle (GRA-148), which is what squeezed the chart under its
 * own floor on a phone. Two things pull it back to two lines: the wordmark and
 * the switcher share the first (the switcher is `ml-auto`, so it sits hard
 * right and the mark hard left, and its chips give up a little horizontal
 * padding below `sm`), and the active mode's stats take the second line
 * rather than sharing it with anything else.
 */
export function AppHeader({ children }: { children?: ReactNode }) {
  return (
    <header className="border-b border-muted/20">
      <Container className="flex flex-wrap items-center gap-x-3 gap-y-2.5 py-2.5 sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:gap-x-4 sm:gap-y-2 sm:py-2">
        <Wordmark as="h1" />
        <div className="ml-auto sm:ml-0">
          <ModeSwitcher />
        </div>
        <div className="flex min-w-0 basis-full flex-wrap items-center gap-x-4 gap-y-1 sm:ml-auto sm:basis-auto sm:justify-end sm:gap-y-2">
          {children}
        </div>
      </Container>
    </header>
  );
}
